import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight } from 'lucide-react';

/**
 * UseCaseTemplate – reusable layout for industry use case pages.
 * Renders: Hero + Intro + Benefits/Features + CTA.
 * Modular & scalable: just pass content props for new use cases.
 */
const UseCaseTemplate = ({
  eyebrow,
  title,
  subtitle,
  heroImage,
  imageAlt,
  intro,
  benefitsTitle,
  benefitsSubtitle,
  benefits = [],
  ctaTitle,
  ctaText,
  primaryCta = { label: 'Contact Us', to: '/contact' },
  secondaryCta = { label: 'View Products', to: '/shop' },
}) => {
  return (
    <div className="usecase-page">
      {/* Hero */}
      <section className="usecase-hero">
        <div className="container usecase-hero-container">
          <div className="usecase-hero-text">
            {eyebrow && <span className="usecase-eyebrow">{eyebrow}</span>}
            <h1 className="display-medium">{title}</h1>
            {subtitle && <p className="body-large">{subtitle}</p>}
            <div className="usecase-hero-actions">
              <Link to={primaryCta.to} className="btn-primary">
                {primaryCta.label}
                <ArrowRight size={18} />
              </Link>
              {secondaryCta && (
                <Link to={secondaryCta.to} className="btn-secondary">
                  {secondaryCta.label}
                </Link>
              )}
            </div>
          </div>
          <div className="usecase-hero-image-wrap">
            {heroImage ? (
              <img
                src={heroImage}
                alt={imageAlt || title}
                className="usecase-hero-image"
                loading="eager"
              />
            ) : (
              <div className="usecase-hero-placeholder" aria-label="Image placeholder">
                <span>Image Placeholder</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Intro */}
      {intro && (
        <section className="usecase-intro">
          <div className="container">
            <div className="usecase-intro-inner">
              <p className="body-large">{intro}</p>
            </div>
          </div>
        </section>
      )}

      {/* Benefits / Features */}
      {benefits.length > 0 && (
        <section className="usecase-benefits">
          <div className="container">
            <div className="section-header">
              <h2 className="heading-1">{benefitsTitle || 'Benefits & Features'}</h2>
              {benefitsSubtitle && <p className="body-large">{benefitsSubtitle}</p>}
            </div>
            <div className="usecase-benefits-grid">
              {benefits.map((b, i) => (
                <div key={i} className="usecase-benefit-card hover-lift">
                  <div className="usecase-benefit-icon">
                    {b.icon || <CheckCircle2 size={28} />}
                  </div>
                  <h3 className="network-card-title">{b.title}</h3>
                  <p className="network-card-content">{b.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="usecase-cta">
        <div className="container">
          <div className="usecase-cta-card">
            <h2 className="heading-1">{ctaTitle || 'Ready to make the switch?'}</h2>
            <p className="body-large">
              {ctaText || 'Talk with our team about a tailored sustainable solution for your business.'}
            </p>
            <div className="usecase-cta-actions">
              <Link to={primaryCta.to} className="btn-primary">
                {primaryCta.label}
                <ArrowRight size={18} />
              </Link>
              {secondaryCta && (
                <Link to={secondaryCta.to} className="btn-outline">
                  {secondaryCta.label}
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UseCaseTemplate;
